/* DRIVER LINK — what the driver gets by text. No login: the token is the truck.
   He taps once, leaves it open, and the pump crew sees him moving. */
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Truck, MapPin, Navigation, Phone, CheckCircle2, Loader2, Radio } from "lucide-react";
import { api } from "@/lib/api";

export default function LiteDrive() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [tracking, setTracking] = useState(false);
  const watchRef = useRef(null);
  const [pings, setPings] = useState(0);

  const load = useCallback(async () => {
    try { const r = await api.get(`/public/drive/${token}`); setData(r.data); }
    catch (e) { setErr(e.response?.data?.detail || "This link isn't valid any more"); }
  }, [token]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const startTracking = () => {
    if (!navigator.geolocation) return toast.error("This phone won't share its location");
    watchRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        setPings((n) => n + 1);
        try {
          await api.post(`/public/drive/${token}/ping`, {
            lat: pos.coords.latitude, lng: pos.coords.longitude,
            heading: pos.coords.heading ?? null,
            speed_kph: pos.coords.speed != null ? Math.round(pos.coords.speed * 3.6) : null,
          });
        } catch { /* keep driving, we'll catch the next fix */ }
      },
      () => toast.error("Allow location so the pump crew can see you"),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 },
    );
    setTracking(true);
  };
  useEffect(() => () => { if (watchRef.current) navigator.geolocation.clearWatch(watchRef.current); }, []);

  const mark = async (status) => {
    try {
      await api.post(`/public/drive/${token}/status`, { status });
      if (status === "done" && watchRef.current) { navigator.geolocation.clearWatch(watchRef.current); setTracking(false); }
      toast.success(status === "on_site" ? "Marked on site" : status === "done" ? "All done — thanks" : "Updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };

  if (err) return (
    <div className="min-h-screen bg-[#0B0D10] text-white flex items-center justify-center p-6 text-center" data-testid="drive-error">
      <div><Truck className="w-9 h-9 mx-auto text-primary-orange" /><div className="heading text-white text-xl font-black uppercase mt-3">{err}</div>
      <p className="text-[13px] text-[#A1A6B0] mt-2">Give your dispatcher a ring and ask them to re-send it.</p></div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-[#0B0D10] flex items-center justify-center"><Loader2 className="w-6 h-6 text-primary-orange animate-spin" /></div>;

  const j = data.job;
  const nav = j && (j.site_lat != null ? `https://www.google.com/maps/dir/?api=1&destination=${j.site_lat},${j.site_lng}` : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(j.site_address || j.site_name)}`);

  return (
    <div className="min-h-screen bg-[#0B0D10] text-white pb-10" data-testid="lite-drive">
      <header className="border-b border-[#22262E] px-4 py-5">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-orange flex items-center justify-center"><Truck className="w-5 h-5 text-black" /></div>
          <div className="min-w-0">
            <div className="text-[9px] tracking-[0.3em] text-[#8B909A]">{data.supplier || "ARMORBATCH GLOBAL"}</div>
            <div className="heading text-white text-lg font-black uppercase truncate" data-testid="drive-truck-name">{data.truck.name}</div>
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-6 space-y-5">
        {!j ? (
          <div className="border border-dashed border-[#2C313A] p-8 text-center" data-testid="drive-no-job">
            <div className="font-black uppercase">Nothing on at the moment</div>
            <p className="text-[13px] text-[#A1A6B0] mt-1">Keep this page handy — your next job will show up here.</p>
          </div>
        ) : (
          <>
            <section className="border border-[#22262E] bg-[#12151A] p-5" data-testid="drive-job">
              <div className="mono text-[10px] uppercase tracking-widest text-primary-orange">{j.time_required || "ASAP"} · Today</div>
              <div className="heading text-white text-2xl font-black uppercase mt-1">{j.site_name}</div>
              <div className="text-[14px] text-[#A1A6B0] mt-1">{j.site_address}</div>
              <div className="mono text-[12px] text-[#8B909A] mt-2">{j.quantity_m3 ? `${j.quantity_m3} m³` : ""}{j.mix_code ? ` · ${j.mix_code}` : ""}</div>
              {j.notes && <div className="text-[13px] text-[#A1A6B0] mt-2 border-l-2 border-primary-orange pl-3">{j.notes}</div>}
              {j.pump_company && (
                <div className="mt-3 border-t border-[#22262E] pt-3 mono text-[12px]">
                  <span className="text-[#8B909A]">PUMP · </span>{j.pump_company} {j.pump_unit?.name || ""}
                  {j.pump_unit?.phone && <a href={`tel:${j.pump_unit.phone}`} className="ml-2 text-primary-orange underline" data-testid="drive-call-pump">{j.pump_unit.phone}</a>}
                </div>
              )}
            </section>

            {!tracking ? (
              <button onClick={startTracking} data-testid="drive-start"
                className="w-full bg-primary-orange text-black font-black uppercase tracking-widest text-base py-5 inline-flex items-center justify-center gap-2">
                <Radio className="w-5 h-5" /> I'm on the way
              </button>
            ) : (
              <div className="border border-green-500 bg-green-500/10 p-4 text-center" data-testid="drive-tracking">
                <div className="font-black uppercase text-green-400">Tracking on</div>
                <p className="mono text-[11px] text-[#A1A6B0] mt-1">Leave this page open — {pings} position{pings === 1 ? "" : "s"} sent</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <a href={nav} target="_blank" rel="noreferrer" data-testid="drive-navigate"
                className="border border-[#2C313A] py-4 text-center font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 hover:border-primary-orange"><Navigation className="w-4 h-4" /> Navigate</a>
              {j.pump_phone && (
                <a href={`tel:${j.pump_phone}`} data-testid="drive-call-site"
                  className="border border-[#2C313A] py-4 text-center font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 hover:border-primary-orange"><Phone className="w-4 h-4" /> Call pump</a>
              )}
            </div>

            <div className="space-y-2 pt-2">
              <button onClick={() => mark("on_site")} data-testid="drive-on-site"
                className="w-full border border-[#2C313A] py-4 font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 hover:border-primary-orange"><MapPin className="w-4 h-4" /> I've arrived</button>
              <button onClick={() => mark("done")} data-testid="drive-done"
                className="w-full border border-[#2C313A] py-4 font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 hover:border-green-500 hover:text-green-400"><CheckCircle2 className="w-4 h-4" /> Load delivered</button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
